/**
 * Personal Data Import Service
 *
 * Purpose: Handles CSV import of personal data for TrueID.Locker users.
 *
 * Features:
 * - CSV template generation aligned to active categories and their field definitions
 * - Row-level CSV parsing and validation
 * - Partial upsert: imports valid rows and collects row-level errors
 * - Downloadable error-report CSV for failed rows
 *
 * Match key: category_name + entry_label + field_name
 *
 * The import CSV format is a flat (long) layout with four canonical columns:
 *   category_name | entry_label | field_name | value
 *
 * This keeps the template simple regardless of how many categories and fields exist,
 * and directly maps to the three-component match key.
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import {
  decryptValue,
  encryptValue,
  hashValue,
  shouldEncryptField,
} from '../utils/personal-data/encryption';
import type {
  CountryContextRule,
  DataCategory,
  FieldDefinition,
} from '@xanectech/types';

const logger = createLogger('Data - PersonalDataImportService');

// ---------------------------------------------------------------------------
// Public constants
// ---------------------------------------------------------------------------

/** Canonical column names for the import CSV. */
export const CSV_IMPORT_COLUMNS = [
  'category_name',
  'entry_label',
  'field_name',
  'value',
] as const;

const CSV_TEMPLATE_COLUMNS = [...CSV_IMPORT_COLUMNS, 'mandatory'] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single parsed row from an import CSV. */
export interface ImportRow {
  rowIndex: number;
  category_name: string;
  entry_label: string;
  field_name: string;
  value: string;
}

/** Per-row result after import processing. */
export interface ImportRowResult {
  rowIndex: number;
  status: 'success' | 'error' | 'skipped';
  /** Only populated when status === 'error'. */
  error?: string;
  category_name: string;
  entry_label: string;
  field_name: string;
  value: string;
}

/** Aggregate result returned by importCSV. */
export interface CsvImportResult {
  totalRows: number;
  importedCount: number;
  errorCount: number;
  skippedCount: number;
  rowResults: ImportRowResult[];
}

/** Aggregate result returned by exportCSV. */
export interface CsvExportResult {
  totalRows: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Escape a value for safe inclusion in a CSV cell.
 * Wraps in double-quotes and doubles any internal double-quotes.
 */
function escapeCsvCell(value: string): string {
  const stringValue = String(value);
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/**
 * Build a CSV string from an array of header+row arrays.
 */
function buildCsv(rows: string[][]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

/**
 * Trigger a browser file download with text content.
 */
function downloadTextFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Legacy auto-generated labels may contain trailing boolean tokens
 * (e.g. "Home true"). Strip these for cleaner export output while preserving
 * manually edited labels.
 */
function normalizeAutoGeneratedEntryLabel(label: string): string {
  return label.replace(/\s+(true|false)$/i, '').trim();
}

/**
 * Normalize a raw Supabase field_definitions row to the FieldDefinition type.
 * Supabase returns JSON columns as opaque Json types; this maps them to the
 * expected Record shapes used throughout the codebase.
 */
function normalizeFieldDefinition(
  field: Record<string, unknown>,
): FieldDefinition {
  const countryContextRulesRaw = (field['country_context_rules'] ||
    {}) as Record<string, string | number | boolean | null>;
  const countryContextRules: Record<string, CountryContextRule> = {};
  for (const [key, value] of Object.entries(countryContextRulesRaw)) {
    if (value !== null) {
      countryContextRules[key] = value as unknown as CountryContextRule;
    }
  }

  return {
    ...(field as unknown as FieldDefinition),
    options: (field['options'] || {}) as Record<
      string,
      string | number | boolean | null
    >,
    country_context_rules: countryContextRules,
  };
}

/**
 * Parse a raw CSV string into a 2-D array of trimmed string values.
 * Handles quoted fields containing commas and escaped quotes (RFC 4180).
 */
function parseRawCsv(csvText: string): string[][] {
  const rows: string[][] = [];
  const lines = csvText.split(/\r?\n/);

  for (const line of lines) {
    if (!line.trim()) continue;

    const cells: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          cells.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    cells.push(current.trim());
    rows.push(cells);
  }

  return rows;
}

// ---------------------------------------------------------------------------
// PersonalDataImportService
// ---------------------------------------------------------------------------

export class PersonalDataImportService {
  // -------------------------------------------------------------------------
  // Template generation
  // -------------------------------------------------------------------------

  /**
   * Generate the import CSV template content.
   *
   * The template contains:
   * 1. A header row with the four canonical columns.
   * 2. One sample row per field in each active category (with placeholder values).
   *
   * @param categories - Active data categories (from EnhancedDataCategoryService.getCategories)
   * @param fieldsByCategory - Map of category_id → FieldDefinition[]
   */
  static generateTemplateContent(
    categories: DataCategory[],
    fieldsByCategory: Map<string, FieldDefinition[]>,
    valuesByEntryAndField: Map<string, string>,
    entriesByCategoryId: Map<
      string,
      Array<{ id: string; entry_label: string }>
    >,
  ): string {
    const rows: string[][] = [Array.from(CSV_TEMPLATE_COLUMNS)];

    for (const category of categories) {
      const fields = fieldsByCategory.get(String(category.id)) ?? [];
      const categoryEntries =
        entriesByCategoryId.get(String(category.id)) ?? [];

      // If no entries exist yet for this category, include a blank starter entry.
      const entriesToRender =
        categoryEntries.length > 0
          ? categoryEntries
          : [{ id: '', entry_label: 'Entry 1' }];

      for (const entry of entriesToRender) {
        for (const field of fields) {
          const valueKey = `${entry.id}|${field.id}`;
          const value = entry.id
            ? (valuesByEntryAndField.get(valueKey) ?? '')
            : '';

          rows.push([
            String(category.name),
            String(entry.entry_label),
            String(field.field_name),
            String(value),
            String(Boolean(field.is_required)).toLowerCase(),
          ]);
        }
      }
    }

    return buildCsv(rows);
  }

  /**
   * Fetch all active categories and their fields then trigger a template download.
   */
  static async downloadTemplate(userId?: string): Promise<void> {
    // Fetch categories
    const { data: categoriesRaw, error: catError } = await supabase
      .from('data_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (catError) {
      logger.error('Failed to fetch categories for template', {
        error: catError.message,
      });
      throw new Error(`Failed to load categories: ${catError.message}`);
    }

    const categories = (categoriesRaw ?? []) as DataCategory[];

    // Fetch all importable fields in one query to avoid partial category misses.
    const { data: allFieldsRaw, error: fieldsError } = await supabase
      .from('field_definitions')
      .select('*')
      .eq('is_active', true)
      .eq('is_importable', true)
      .in(
        'category_id',
        categories.map((cat) => cat.id),
      )
      .order('display_order', { ascending: true });

    if (fieldsError) {
      logger.error('Failed to fetch importable fields for template', {
        error: fieldsError.message,
      });
      throw new Error(`Failed to load template fields: ${fieldsError.message}`);
    }

    const fieldsByCategory = new Map<string, FieldDefinition[]>();

    for (const rawField of allFieldsRaw ?? []) {
      const field = normalizeFieldDefinition(
        rawField as Record<string, unknown>,
      );
      const list = fieldsByCategory.get(field.category_id) ?? [];
      list.push(field);
      fieldsByCategory.set(field.category_id, list);
    }

    const valuesByEntryAndField = new Map<string, string>();
    const entriesByCategoryId = new Map<
      string,
      Array<{ id: string; entry_label: string }>
    >();

    if (userId) {
      const { data: entriesRaw, error: entriesError } = await supabase
        .from('category_entries')
        .select('id, category_id, entry_label')
        .eq('user_id', userId)
        .in(
          'category_id',
          categories.map((cat) => cat.id),
        )
        .order('created_at', { ascending: true });

      if (entriesError) {
        logger.warn('Failed to fetch user entries for template prefill', {
          error: entriesError.message,
        });
      } else {
        const entries = (entriesRaw ?? []) as Array<{
          id: string;
          category_id: string;
          entry_label: string;
        }>;

        for (const entry of entries) {
          const list = entriesByCategoryId.get(entry.category_id) ?? [];
          list.push({ id: entry.id, entry_label: entry.entry_label });
          entriesByCategoryId.set(entry.category_id, list);
        }

        if (entries.length > 0) {
          const { data: fieldDataRaw, error: fieldDataError } = await supabase
            .from('category_field_data')
            .select('entry_id, field_id, value_encrypted')
            .in(
              'entry_id',
              entries.map((entry) => entry.id),
            );

          if (fieldDataError) {
            logger.warn('Failed to fetch user field values for template', {
              error: fieldDataError.message,
            });
          } else {
            const fieldData = (fieldDataRaw ?? []) as Array<{
              entry_id: string;
              field_id: string;
              value_encrypted: string | null;
            }>;

            await Promise.all(
              fieldData.map(async (item) => {
                if (!item.value_encrypted) {
                  return;
                }

                let value = item.value_encrypted;
                try {
                  value = await decryptValue(item.value_encrypted);
                } catch {
                  // Keep raw value for legacy/non-encrypted rows.
                }

                valuesByEntryAndField.set(
                  `${item.entry_id}|${item.field_id}`,
                  value,
                );
              }),
            );
          }
        }
      }
    }

    const content = this.generateTemplateContent(
      categories,
      fieldsByCategory,
      valuesByEntryAndField,
      entriesByCategoryId,
    );
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadTextFile(content, `personal-data-import-template-${timestamp}.csv`);
  }

  // -------------------------------------------------------------------------
  // CSV parsing
  // -------------------------------------------------------------------------

  /**
   * Parse a CSV file's text content into ImportRow objects.
   *
   * Expects the first row to be the header row with the four canonical column names.
   * Rows that do not have all four columns populated are skipped.
   *
   * @returns Array of parsed ImportRow objects and an array of parse-level errors.
   */
  static parseCSV(csvText: string): {
    rows: ImportRow[];
    parseErrors: string[];
  } {
    const raw = parseRawCsv(csvText);
    const parseErrors: string[] = [];

    if (raw.length === 0) {
      return { rows: [], parseErrors: ['The file appears to be empty.'] };
    }

    // Validate header row
    const header = raw[0]?.map((h) => h.toLowerCase().trim()) ?? [];
    const expectedHeaders = Array.from(CSV_IMPORT_COLUMNS);
    const missingHeaders = expectedHeaders.filter((h) => !header.includes(h));

    if (missingHeaders.length > 0) {
      parseErrors.push(
        `Missing required columns: ${missingHeaders.join(', ')}. ` +
          `Expected columns: ${expectedHeaders.join(', ')}.`,
      );
      return { rows: [], parseErrors };
    }

    const colIndex: Record<string, number> = {};
    for (const col of CSV_IMPORT_COLUMNS) {
      colIndex[col] = header.indexOf(col);
    }

    const rows: ImportRow[] = [];

    for (let i = 1; i < raw.length; i++) {
      const cells = raw[i];
      if (!cells) continue;

      const category_name = cells[colIndex['category_name'] ?? 0]?.trim() ?? '';
      const entry_label = cells[colIndex['entry_label'] ?? 1]?.trim() ?? '';
      const field_name = cells[colIndex['field_name'] ?? 2]?.trim() ?? '';
      const value = cells[colIndex['value'] ?? 3]?.trim() ?? '';

      // Skip entirely empty rows (e.g. trailing blank lines)
      if (!category_name && !entry_label && !field_name) continue;

      rows.push({
        rowIndex: i + 1, // 1-based, accounting for header row
        category_name,
        entry_label,
        field_name,
        value,
      });
    }

    return { rows, parseErrors };
  }

  // -------------------------------------------------------------------------
  // Import (partial upsert)
  // -------------------------------------------------------------------------

  /**
   * Import rows from a parsed CSV.
   *
   * Strategy:
   * - Validate each row against category/field definitions.
   * - For valid rows, upsert: find or create entry by (user_id, category_id, entry_label),
   *   then upsert field data by (entry_id, field_id).
   * - Invalid rows are recorded with their error messages and excluded from the upsert.
   *
   * @param rows - Parsed import rows
   * @param userId - Authenticated user's ID
   */
  static async importCSV(
    rows: ImportRow[],
    userId: string,
  ): Promise<CsvImportResult> {
    const rowResults: ImportRowResult[] = [];
    let importedCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    if (!userId) {
      throw new Error('User must be authenticated to import data.');
    }

    // 1. Prefetch all active categories (keyed by lowercased name for case-insensitive lookup)
    const { data: categoriesRaw, error: catError } = await supabase
      .from('data_categories')
      .select('*')
      .eq('is_active', true);

    if (catError) {
      throw new Error(`Failed to load categories: ${catError.message}`);
    }

    const categoriesByName = new Map<string, DataCategory>(
      ((categoriesRaw ?? []) as DataCategory[]).map((c) => [
        c.name.toLowerCase(),
        c,
      ]),
    );

    // 2. Prefetch all field definitions for referenced categories
    const referencedCategoryNames = new Set(
      rows.map((r) => r.category_name.toLowerCase()),
    );
    const referencedCategories = Array.from(categoriesByName.entries())
      .filter(([name]) => referencedCategoryNames.has(name))
      .map(([, cat]) => cat);

    const fieldsByCategoryId = new Map<string, FieldDefinition[]>();
    if (referencedCategories.length > 0) {
      const { data: allFields, error: fieldsError } = await supabase
        .from('field_definitions')
        .select('*')
        .in(
          'category_id',
          referencedCategories.map((c) => c.id),
        )
        .eq('is_active', true)
        .eq('is_importable', true);

      if (fieldsError) {
        throw new Error(
          `Failed to load field definitions: ${fieldsError.message}`,
        );
      }

      for (const field of (allFields ?? []).map((f) =>
        normalizeFieldDefinition(f as Record<string, unknown>),
      )) {
        const list = fieldsByCategoryId.get(field.category_id) ?? [];
        list.push(field);
        fieldsByCategoryId.set(field.category_id, list);
      }
    }

    // 3. Cache for entry lookups: "userId|categoryId|entryLabel" -> entry id
    const entryCache = new Map<string, string>();

    // 4. Process each row
    for (const row of rows) {
      const rowResult: ImportRowResult = {
        rowIndex: row.rowIndex,
        status: 'error',
        category_name: row.category_name,
        entry_label: row.entry_label,
        field_name: row.field_name,
        value: row.value,
      };

      try {
        // 4a. Validate required fields
        if (!row.category_name) {
          throw new Error('category_name is required.');
        }
        if (!row.entry_label) {
          throw new Error('entry_label is required.');
        }
        if (!row.field_name) {
          throw new Error('field_name is required.');
        }

        // 4b. Resolve category
        const category = categoriesByName.get(row.category_name.toLowerCase());
        if (!category) {
          throw new Error(`Category "${row.category_name}" not found.`);
        }

        // 4c. Resolve field definition
        const categoryFields = fieldsByCategoryId.get(category.id) ?? [];
        const fieldDef = categoryFields.find(
          (f) => f.field_name.toLowerCase() === row.field_name.toLowerCase(),
        );
        if (!fieldDef) {
          throw new Error(
            `Field "${row.field_name}" not found in category "${category.name}".`,
          );
        }

        // 4d. Validate field value (required check)
        const hasValue = row.value.trim().length > 0;

        if (fieldDef.is_required && !hasValue) {
          throw new Error(
            `Field "${fieldDef.field_label}" is required but has no value.`,
          );
        }

        // 4e. Resolve or create entry
        const entryKey = `${userId}|${category.id}|${row.entry_label.toLowerCase()}`;
        let entryId = entryCache.get(entryKey);

        if (!entryId) {
          // Look for existing entry
          const { data: existingEntries, error: lookupError } = await supabase
            .from('category_entries')
            .select('id')
            .eq('user_id', userId)
            .eq('category_id', category.id)
            .ilike('entry_label', row.entry_label)
            .limit(1);

          if (lookupError) {
            throw new Error(`Entry lookup failed: ${lookupError.message}`);
          }

          if (existingEntries && existingEntries.length > 0) {
            const existing = existingEntries[0];
            if (!existing?.id) {
              throw new Error('Entry lookup returned an entry without an ID.');
            }
            entryId = existing.id;
          } else {
            // Create new entry
            const { data: newEntry, error: createError } = await supabase
              .from('category_entries')
              .insert({
                user_id: userId,
                category_id: category.id,
                entry_label: row.entry_label,
                label_manually_edited: true,
                is_primary: false,
                is_favorite: false,
              })
              .select('id')
              .single();

            if (createError) {
              throw new Error(`Failed to create entry: ${createError.message}`);
            }
            entryId = newEntry.id;
          }

          entryCache.set(entryKey, entryId as string);
        }

        // 4f. Encrypt and upsert field data
        const shouldEncrypt = shouldEncryptField({
          is_sensitive: fieldDef.is_sensitive ?? false,
          is_pii: fieldDef.is_pii ?? false,
          data_type: fieldDef.data_type,
        });

        const encryptedValue = shouldEncrypt
          ? hasValue
            ? await encryptValue(row.value)
            : ''
          : row.value;
        const valueHash =
          shouldEncrypt && hasValue ? await hashValue(row.value) : null;

        const { error: upsertError } = await supabase
          .from('category_field_data')
          .upsert(
            {
              entry_id: entryId as string,
              field_id: fieldDef.id,
              value_encrypted: encryptedValue,
              value_hash: valueHash,
              is_verified: false,
              verified_at: null,
              verified_by: null,
            },
            { onConflict: 'entry_id,field_id' },
          );

        if (upsertError) {
          throw new Error(`Failed to save field data: ${upsertError.message}`);
        }

        rowResult.status = 'success';
        importedCount++;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        logger.warn('Import row failed', {
          rowIndex: row.rowIndex,
          category_name: row.category_name,
          entry_label: row.entry_label,
          field_name: row.field_name,
          error: message,
        });
        rowResult.status = 'error';
        rowResult.error = message;
        errorCount++;
      }

      rowResults.push(rowResult);
    }

    return {
      totalRows: rows.length,
      importedCount,
      errorCount,
      skippedCount,
      rowResults,
    };
  }

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  /**
   * Generate CSV content from the authenticated user's existing personal data.
   *
   * Output uses the same canonical import columns:
   * category_name | entry_label | field_name | value
   */
  static async generateExportContent(userId: string): Promise<string> {
    if (!userId) {
      throw new Error('User must be authenticated to export data.');
    }

    const { data: entriesRaw, error: entriesError } = await supabase
      .from('category_entries')
      .select('id, category_id, entry_label, label_manually_edited')
      .eq('user_id', userId);

    if (entriesError) {
      throw new Error(`Failed to load entries: ${entriesError.message}`);
    }

    const entries = (entriesRaw ?? []) as Array<{
      id: string;
      category_id: string;
      entry_label: string;
      label_manually_edited?: boolean | null;
    }>;

    const rows: string[][] = [Array.from(CSV_IMPORT_COLUMNS)];
    if (entries.length === 0) {
      return buildCsv(rows);
    }

    const entryById = new Map(entries.map((entry) => [entry.id, entry]));
    const categoryIds = Array.from(
      new Set(entries.map((entry) => entry.category_id)),
    );

    const { data: categoryData, error: categoryError } = await supabase
      .from('data_categories')
      .select('id, name')
      .in('id', categoryIds);

    if (categoryError) {
      throw new Error(`Failed to load categories: ${categoryError.message}`);
    }

    const categoryNameById = new Map(
      (categoryData ?? []).map((category) => [
        String(category.id),
        String(category.name),
      ]),
    );

    const entryIds = entries.map((entry) => entry.id);
    const { data: fieldDataRaw, error: fieldDataError } = await supabase
      .from('category_field_data')
      .select('entry_id, field_id, value_encrypted')
      .in('entry_id', entryIds);

    if (fieldDataError) {
      throw new Error(`Failed to load field data: ${fieldDataError.message}`);
    }

    const fieldData = (fieldDataRaw ?? []) as Array<{
      entry_id: string;
      field_id: string;
      value_encrypted: string | null;
    }>;

    if (fieldData.length === 0) {
      return buildCsv(rows);
    }

    const fieldIds = Array.from(
      new Set(fieldData.map((item) => item.field_id)),
    );
    const { data: fieldsRaw, error: fieldsError } = await supabase
      .from('field_definitions')
      .select('id, field_name, is_sensitive, is_pii, data_type')
      .in('id', fieldIds)
      .eq('is_exportable', true);

    if (fieldsError) {
      throw new Error(
        `Failed to load field definitions: ${fieldsError.message}`,
      );
    }

    const fieldById = new Map(
      (fieldsRaw ?? []).map((field) => [String(field.id), field]),
    );

    const exportRows: string[][] = [];

    for (const fieldValue of fieldData) {
      const entry = entryById.get(fieldValue.entry_id);
      const field = fieldById.get(fieldValue.field_id) as
        | {
            id: string;
            field_name: string;
            is_sensitive: boolean;
            is_pii: boolean;
            data_type: string | null;
          }
        | undefined;

      if (!entry || !field) {
        continue;
      }

      const categoryName = categoryNameById.get(entry.category_id) ?? 'Unknown';

      let value = fieldValue.value_encrypted ?? '';
      const encrypted = shouldEncryptField({
        is_sensitive: field.is_sensitive ?? false,
        is_pii: field.is_pii ?? false,
        data_type: field.data_type ?? undefined,
      });

      if (encrypted && value) {
        try {
          value = await decryptValue(value);
        } catch (error) {
          logger.warn('Failed to decrypt value during export', {
            entryId: entry.id,
            fieldId: field.id,
            error: error instanceof Error ? error.message : String(error),
          });
          value = '';
        }
      }

      exportRows.push([
        categoryName,
        entry.label_manually_edited
          ? String(entry.entry_label ?? '')
          : normalizeAutoGeneratedEntryLabel(String(entry.entry_label ?? '')),
        String(field.field_name ?? ''),
        value,
      ]);
    }

    exportRows.sort((a, b) => {
      const keyA = `${a[0]}|${a[1]}|${a[2]}`.toLowerCase();
      const keyB = `${b[0]}|${b[1]}|${b[2]}`.toLowerCase();
      return keyA.localeCompare(keyB);
    });

    return buildCsv([...rows, ...exportRows]);
  }

  /**
   * Export all personal data for the authenticated user as CSV.
   */
  static async exportCSV(userId: string): Promise<CsvExportResult> {
    const content = await this.generateExportContent(userId);
    const totalRows = Math.max(content.split('\n').length - 1, 0);
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '-');
    downloadTextFile(content, `personal-data-export-${timestamp}.csv`);
    return { totalRows };
  }

  // -------------------------------------------------------------------------
  // Error report
  // -------------------------------------------------------------------------

  /**
   * Generate a CSV error report for failed rows.
   * Includes all original columns plus an `error` column.
   */
  static generateErrorReport(rowResults: ImportRowResult[]): string {
    const failed = rowResults.filter((r) => r.status === 'error');

    const rows: string[][] = [
      [...Array.from(CSV_IMPORT_COLUMNS), 'row_number', 'error'],
      ...failed.map((r) => [
        r.category_name,
        r.entry_label,
        r.field_name,
        r.value,
        String(r.rowIndex),
        r.error ?? 'Unknown error',
      ]),
    ];

    return buildCsv(rows);
  }

  /**
   * Trigger a browser download of the error report CSV.
   */
  static downloadErrorReport(rowResults: ImportRowResult[]): void {
    const content = this.generateErrorReport(rowResults);
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '-');
    downloadTextFile(content, `import-errors-${timestamp}.csv`);
  }
}
